import { useEffect, useRef } from 'react';
import { useLocation } from 'react-router-dom';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

// Generate or get session ID
const getSessionId = () => {
  let sessionId = sessionStorage.getItem('analytics_session');
  if (!sessionId) {
    sessionId = `sess_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`;
    sessionStorage.setItem('analytics_session', sessionId);
  }
  return sessionId;
};

// Determine page type from path
const getPageInfo = (pathname) => {
  if (pathname === '/' || pathname === '') {
    return { page_type: 'home', page_id: null, page_title: 'Inicio' };
  }
  
  if (pathname.startsWith('/galeria/')) {
    const id = pathname.split('/galeria/')[1];
    return { page_type: 'gallery', page_id: id, page_title: null };
  }
  
  if (pathname.startsWith('/blog/')) {
    const id = pathname.split('/blog/')[1];
    return { page_type: 'blog', page_id: id, page_title: null };
  }
  
  if (pathname === '/tarifas') {
    return { page_type: 'service', page_id: null, page_title: 'Tarifas' };
  }
  
  if (pathname === '/contacto' || pathname.includes('#contacto')) {
    return { page_type: 'contact', page_id: null, page_title: 'Contacto' };
  }
  
  if (pathname.startsWith('/admin')) {
    return null; // Don't track admin pages
  }
  
  if (pathname.startsWith('/cliente')) {
    return null; // Don't track client area
  }
  
  return { page_type: 'other', page_id: null, page_title: pathname };
};

export const useAnalytics = () => {
  const location = useLocation();
  const lastTrackedPath = useRef('');

  useEffect(() => {
    const trackPageView = async () => {
      // Avoid duplicate tracking
      if (lastTrackedPath.current === location.pathname) {
        return;
      }
      lastTrackedPath.current = location.pathname;

      const pageInfo = getPageInfo(location.pathname);
      
      // Don't track admin or client pages
      if (!pageInfo) {
        return;
      }

      // Respect cookie consent: only track when user accepted analytics cookies
      try {
        const consent = localStorage.getItem('jq-cookie-consent');
        if (consent !== 'accepted') {
          return;
        }
      } catch {
        return;
      }

      try {
        // Get page title from document if available
        const pageTitle = pageInfo.page_title || document.title;

        await fetch(`${API}/analytics/track`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({
            ...pageInfo,
            page_title: pageTitle,
            path: location.pathname,
            referrer: document.referrer || null,
            session_id: getSessionId()
          })
        });
      } catch (error) {
        // Silently fail - analytics shouldn't break the app
        console.debug('Analytics tracking failed:', error);
      }
    };

    // Small delay to ensure page has rendered
    const timeoutId = setTimeout(trackPageView, 100);
    return () => clearTimeout(timeoutId);
  }, [location.pathname]);
};

export default useAnalytics;
